import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismIcon, PrismTable } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { REVERSE_ISO_DAYS } from 'frontend-utils/utils/constants/constant-values';
import SettingsRecommendationsSnowflakesRecommendationsSettingCard from 'lumen/components/settings/recommendations/snowflakes/recommendations-setting-card';
import SettingsRecommendationsTdMinimumPriceDifference from 'lumen/components/settings/recommendations/td-minimum-price-difference';
import type { RecommendationStrategy } from 'lumen/models';
import type { RecommendationsModel } from 'lumen/routes/hotel/settings/recommendations';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

import styles from './minimum-price-difference.module.css';

type TransformedRowObject = {
  [key: string]: {
    formattedValue: string | SafeString | null;
    rawValue: number | null;
  };
};

interface SettingsRecommendationsMinimumPriceDifferenceSignature {
  Args: {
    model: RecommendationsModel;
    strategy: RecommendationStrategy;
  };
}

export default class SettingsRecommendationsMinimumPriceDifference extends Component<SettingsRecommendationsMinimumPriceDifferenceSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isEditMode = false;
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked strategy = this.args.strategy;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const days = [];
    let dow = dayjs().startOf('week');

    while (dow.isSameOrBefore(dayjs().endOf('week'))) {
      const weekdayId = dow.isoWeekday();
      const isoWeekday = REVERSE_ISO_DAYS[weekdayId];

      days.push({
        name: this.intl.t(`days_short.${isoWeekday}`),
        valuePath: weekdayId.toString(),
        textAlign: 'left' as const,
        id: weekdayId,
        isEditing: this.isEditMode,
        strategy: this.strategy,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        minimumPriceDifferenceChange: this.minimumPriceDifferenceChange,
        hasWarning: this.columnsWithWarning.includes(weekdayId),
        tdClassNames: 'td-minimum-price-difference',
        tdComponent: SettingsRecommendationsTdMinimumPriceDifference,
      });
      dow = dow.add(1, 'day');
    }

    return days;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columnsWithWarning() {
    const columnsWithWarnings: number[] = [];

    const settings = this.minDiffSettings;
    if (!settings) {
      return columnsWithWarnings;
    }

    const dayOfWeekEntries = Object.entries(settings.day_of_week);

    for (let i = 0; i < dayOfWeekEntries.length; i++) {
      const entry = dayOfWeekEntries[i];

      const [dayOfWeek, value] = entry!;
      const weekdayId = parseInt(dayOfWeek, 10);

      const warningThreshold = this.primaWarningValues?.find(
        (warning) => warning.dayOfWeek === weekdayId,
      )?.maxMinPriceDiff;

      if (
        warningThreshold !== undefined &&
        value !== null &&
        value > warningThreshold
      ) {
        columnsWithWarnings.push(weekdayId);
      }
    }

    return columnsWithWarnings;
  }

  // Edge case: If user makes a change and then navigates away to another page (seasonal strategy subroute) without saving/canceling and then comes back
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMinimumPriceDifferenceEnabled() {
    return this.minDiffSettings?.enabled ?? false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSaveButtonDisabled() {
    if (this.strategy) {
      return !this.strategy.hasDirtyAttributes;
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minDiffSettings() {
    const { settings } = this.strategy;
    return settings.settings_type === 'minimum_difference' ? settings : null;
  }

  // For values higher than 10% of their average selling price we should show a warning
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get primaWarningValues() {
    if (this.args.model.primaWarningValuesTask.isRunning) {
      return [];
    }
    return this.args.model.primaWarningValuesTask.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const settings = this.minDiffSettings;
    if (!settings) {
      return [];
    }

    const dowSettings = settings.day_of_week;

    return [
      Object.entries(dowSettings).reduce<TransformedRowObject>(
        (rowObject, [key, value]) => {
          rowObject[key] = {
            rawValue: value,
            formattedValue:
              value !== null
                ? this.hotelAndMarketService.formatRate(value ?? 0)
                : this.intl.t(
                    'pricing_assistant.recommendations.price_range.no_min',
                  ),
          };
          return rowObject;
        },
        {},
      ),
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showWarning() {
    return this.isEditMode && this.columnsWithWarning.length > 0;
  }

  // Rollback the unsaved changes on destroy
  willDestroy(): void {
    super.willDestroy();

    // strategy tracked variable is not update when the referenced strategy record is removed from the store (e.g. on route change)
    // therefore, we check the passed args.strategy to see if it is still available
    // we also check if the strategy is not destroyed or destroying to make sure record is in store
    // Note: Can we use @localCopy instead of tracked variable?
    if (
      this.strategy &&
      !this.strategy.isDestroyed &&
      !this.strategy.isDestroying
    ) {
      this.strategy.rollbackAttributes();
    }
  }

  @action minimumPriceDifferenceChange(
    dayOfWeek: number,
    value: number | null,
  ): void {
    const strategy = this.strategy;
    const settings = this.minDiffSettings;

    if (!strategy || !settings) {
      return;
    }
    strategy.settings = {
      ...settings,
      day_of_week: {
        ...settings.day_of_week,
        [dayOfWeek]: value,
      },
    };
  }

  @action onCancelClick(): void {
    // Reset the minimum price difference strategy
    if (this.strategy) {
      this.strategy.rollbackAttributes();
    }

    this.isEditMode = false;
  }

  @action onEditClick(): void {
    this.isEditMode = true;
  }

  @action
  async onSaveClick(): Promise<void> {
    if (this.strategy) {
      await this.args.model.saveStrategyTask.perform(this.strategy);
    }

    this.isEditMode = false;

    this.usageAnalytics.track('Minimum price change value changed', {
      eventTrigger: 'recommendation-settings',
      product: 'pricing-manager',
    });
  }

  @action
  async onToggle(): Promise<void> {
    if (this.strategy) {
      // rollback changes
      this.strategy.rollbackAttributes();

      // update the enabled status
      const settings = this.minDiffSettings;
      if (settings) {
        this.strategy.settings = {
          ...settings,
          enabled: !settings.enabled,
        };
      }

      await this.args.model.saveStrategyTask.perform(this.strategy);
    }

    // Reset edit mode
    this.isEditMode = false;

    if (this.isMinimumPriceDifferenceEnabled) {
      this.usageAnalytics.track('Minimum price change enabled', {
        eventTrigger: 'recommendation-settings',
        product: 'pricing-manager',
      });
    }
  }

  <template>
    <SettingsRecommendationsSnowflakesRecommendationsSettingCard
      @description={{t
        "pricing_assistant.recommendations.settings.min_price_change.description"
      }}
      @handleToggleChange={{this.onToggle}}
      @hasToggle={{true}}
      @illustration="40x40_intersect"
      @isToggleDisabled={{this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled}}
      @isToggled={{this.isMinimumPriceDifferenceEnabled}}
      @title={{t
        "pricing_assistant.recommendations.settings.min_price_change.title"
      }}
    >
      <:content>
        <div class="flex flex-col gap-200">
          <div class="flex flex-row items-center justify-between">
            <h4>{{t
                "pricing_assistant.recommendations.minimum_price_difference.table_title"
              }}</h4>
            {{#unless
              this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled
            }}
              {{#if this.isEditMode}}
                <div class="flex flex-row gap-200">
                  <PrismButton
                    @buttonType="secondary"
                    @label={{t "general.cancel"}}
                    @onClick={{this.onCancelClick}}
                  />
                  <PrismButton
                    @isDisabled={{this.isSaveButtonDisabled}}
                    @label={{t "general.save"}}
                    @onClick={{this.onSaveClick}}
                  />
                </div>
              {{else}}
                <PrismButton
                  @buttonType="secondary"
                  @iconBefore="edit-pencil"
                  @label={{t "general.edit"}}
                  @onClick={{this.onEditClick}}
                />
              {{/if}}
            {{/unless}}
          </div>
          <PrismTable
            @columns={{this.columns}}
            @rows={{this.rows}}
            @widthConstraint="eq-container"
          />

          {{#if this.showWarning}}
            <p
              class="text-200--regular
                {{styles.warning-text}}
                flex flex-row gap-100"
            >
              <PrismIcon @color="warning" @name="warning" @size="small" />
              {{t "pricing_assistant.recommendations.price_range.warning.high"}}
            </p>
          {{/if}}
        </div>

      </:content>
    </SettingsRecommendationsSnowflakesRecommendationsSettingCard>
  </template>
}
